<script setup lang="ts">
import { ref, computed } from 'vue'
import {
  Typography,
  DropdownContent,
  DimmingOverlay,
  vClickOutside
} from '@temperworks/components'

import { TemperIcon, Flag } from '@temperworks/icons'
import { DropdownItemsInterface, DropdownVariant, DimmingOverlayVariant, TypographyType, IconNames, IconPosition } from '@temperworks/types'
interface Props {
  variant: DropdownVariant
  items: DropdownItemsInterface[]
  icon?: string
  iconPosition?: string
  flag?: string
  topLabel?: string
  placeholder: string
  extraPlaceholderContent?: string
  selected?: string
  selectedMore?: string
  displayControls?: boolean
  disabled?: boolean
  selectAllOption?: boolean
  placeholderSearch?: string
  multiSelect?: boolean
  error?: boolean
  withinBounds?: boolean
  allowDeselect?: boolean
  enableSearch?: boolean
}
const open = ref<boolean>(false)
const checked = ref<boolean>(false)
const searchQuery = ref<string>('')
const emit = defineEmits(['selected', 'multiSelected'])
const props = withDefaults(defineProps<Props>(), {
  displayControls: true,
  icon: undefined,
  iconPosition: undefined,
  flag: undefined,
  topLabel: undefined,
  selected: undefined,
  placeholderSearch: '',
  extraPlaceholderContent: undefined,
  selectedMore: undefined,
  multiSelect: false,
  error: false,
  enableSearch: false,
})

const filteredItems = computed(() => {
  if (!props.enableSearch || !searchQuery.value) {
    return props.items
  }

  const query = searchQuery.value.toLowerCase()
  return props.items.filter(item => {
    return item.name.toLowerCase().includes(query)
  })
})

function toggleDropdown (event?: Event) {
  if (event) {
    event.stopPropagation()
  }
  open.value = !open.value
  checked.value = !checked.value
  if (!open.value) {
    searchQuery.value = ''
  }
}

function hideDropdown () {
  open.value = false
}

function selectedItem (item) {
  if (props.allowDeselect) {
    if (item.name === props.selected) {
      emit('selected', '')
      resetValues()
      return
    }
  }

  searchQuery.value = ''
  emit('selected', item)
  resetValues()
}

function resetValues () {
  open.value = false
  checked.value = false
}

function multipleSelected (item) {
  emit('multiSelected', item)
}
</script>

<template>
  <div
    class="relative"
    v-click-outside="hideDropdown"
  >
    <Typography
      v-if="props.topLabel"
      :variant="TypographyType.bodySmallMedium"
      :content="props.topLabel"
      class="label"
    />
    <div
      class="dropdown"
      :class="[
        { 'selected': checked },
        { 'disabled': props.disabled },
        { 'error': props.error },
        { 'table-filter': props.variant === DropdownVariant.tableFilter },
        { 'value-set': props.selected }
      ]
      "
      @click="toggleDropdown()"
    >
      <div
        v-if="props.variant === DropdownVariant.tableFilter"
        class="icon"
      >
        <TemperIcon
          :name="IconNames.filter"
          :position="IconPosition.left"
          fill="grey900"
        />
      </div>
      <div
        v-if="props.icon || props.flag"
        class="icon"
      >
        <TemperIcon
          v-if="props.icon"
          :name="props.icon"
          :position="props.iconPosition"
          fill="grey400"
        />
        <Flag
          v-if="props.flag"
          :name="props.flag"
        />
      </div>
      <div class="name">
        <div
          v-if="props.selected && !props.enableSearch"
          class="selected flex-placeholder"
        >
          <Typography
            :variant="TypographyType.body"
            :content="props.selected"
          />
          <Typography
            v-if="props.selectedMore"
            :variant="TypographyType.body"
            :content="props.selectedMore"
            class="lightgrey"
          />
        </div>
        <div
          v-else
          class="flex-placeholder"
        >
          <input
            v-if="props.enableSearch"
            v-model="searchQuery"
            type="text"
            :placeholder="props.selected || props.placeholder"
            @click.stop="toggleDropdown($event)"
            @keydown.enter.prevent="toggleDropdown"
          >
          <template v-else>
            <Typography
              :variant="TypographyType.body"
              :content="props.placeholder"
            />
            <Typography
              v-if="props.extraPlaceholderContent"
              :variant="TypographyType.body"
              :content="props.extraPlaceholderContent"
              class="lightgrey"
            />
          </template>
        </div>
      </div>
      <div
        v-if="props.displayControls && props.variant !== DropdownVariant.tableFilter"
        class="input-controls"
      >
        <TemperIcon
          :name="props.enableSearch ? IconNames.search : 'dropdownCaretsC'"
          size="large"
          position="center"
          :color="props.disabled ? 'grey800' : 'dark'"
        />
      </div>
    </div>
    <DropdownContent
      v-if="open && (props.items.length > 0 || $slots['extra-content'])"
      id="dropdown-content"
      :variant="props.variant"
      :items="filteredItems"
      :open="open"
      :select-all-option="props.selectAllOption"
      :placeholder-search="props.placeholderSearch"
      :multi-select="props.multiSelect"
      :selected="props.selected"
      :within-bounds="props.withinBounds"
      @selected="selectedItem($event)"
      @multi-selected="multipleSelected($event)"
    >
      <template #extra-content>
        <div @click="resetValues">
          <slot name="extra-content" />
        </div>
      </template>
    </DropdownContent>
    <DimmingOverlay
      v-if="open"
      :variant="DimmingOverlayVariant.bright"
      @close="toggleDropdown()"
    />
  </div>
</template>

<style lang="scss" scoped>
@use './Dropdown.scss';
</style>
